import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, RefreshCw, Plus } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAsync } from "@/lib/hooks";
import { api } from "@/lib/api";
import { Panel, Spinner, Tag, Btn, EmptyState } from "@/components/kit";
import { MileageTrend } from "@/components/MileageTrend";
import { NewRequestModal } from "@/components/NewRequestModal";
import { DisposeVehicleModal } from "@/components/DisposeVehicleModal";
import { VehicleHistory } from "@/components/VehicleHistory";
import { formatReg, normalizeReg, fmtDate, fmtDateTime, REQUEST_LABEL } from "@/lib/vda";

async function loadVehicleProfile(id) {
  const { data: v } = await supabase.from("vehicles").select("*").eq("id", id).maybeSingle();
  if (!v) return { vehicle: null };
  const [{ data: cust }, { data: allocs }, { data: exBookings }, { data: caps }, { data: events }] = await Promise.all([
    supabase.from("customers").select("id, name, hire_policy, lifecycle_status").eq("id", v.customer_id).maybeSingle(),
    supabase.from("driver_allocations").select("id, status, drivers(full_name, mobile_number)").eq("vehicle_id", id).eq("status", "active").limit(1),
    supabase.from("v_external_bookings").select("*").order("created_at", { ascending: false }),
    supabase.from("customer_capabilities").select("capability_key, enabled").eq("customer_id", v.customer_id),
    supabase.from("vehicle_history_events").select("*").eq("vehicle_id", id).order("event_at", { ascending: false }).limit(300),
  ]);
  const nreg = normalizeReg(v.registration);
  const bookings = (exBookings || []).filter((b) => normalizeReg(b.registration) === nreg);
  const openBooking = bookings.find((b) => b.public_status !== "Completed") || null;
  let updates = [];
  if (openBooking) {
    const { data: up } = await supabase.from("v_external_updates").select("*").eq("booking_request_id", openBooking.id).order("created_at", { ascending: false });
    updates = up || [];
  }
  const capMap = Object.fromEntries((caps || []).map((c) => [c.capability_key, c.enabled]));
  return { vehicle: v, customer: cust, allocation: allocs?.[0] || null, bookings, openBooking, updates, caps: capMap, events: events || [] };
}

function Row({ label, value, tone }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`text-sm font-medium ${tone === "red" ? "text-[hsl(var(--vda-red))]" : ""}`}>{value || "—"}</span>
    </div>
  );
}

export default function ClientVehicle() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data, loading, reload } = useAsync(() => loadVehicleProfile(id), [id]);
  const [chasing, setChasing] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showDispose, setShowDispose] = useState(false);

  if (loading) return <div className="py-20 grid place-items-center"><Spinner /></div>;
  const v = data?.vehicle;
  if (!v) return <div className="py-20 grid place-items-center"><EmptyState title="Vehicle not found" /></div>;
  const { customer, allocation, openBooking, updates, bookings, caps, events } = data;
  const driver = allocation?.drivers || null;
  const canDispose = customer?.lifecycle_status === "run_off" && caps?.disposal_returns;

  const requestUpdate = async () => {
    if (!openBooking) return;
    setChasing(true);
    try {
      await api.fmChase(openBooking.id);
      toast.success("We've notified the Venson team and flagged this request for review.");
      reload();
    } catch (e) { toast.info(e.message || "Couldn't request an update right now."); }
    finally { setChasing(false); }
  };

  return (
    <>
      <div className="mx-auto max-w-[1000px]">
        <button data-testid="cv-back" onClick={() => nav("/fleet/vehicles")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ChevronLeft className="h-4 w-4" /> Back to vehicles</button>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <p className="vda-tick text-[hsl(var(--vda-quiet))]">Vehicle profile</p>
            <h1 className="vda-reg text-3xl font-semibold tracking-wide text-primary">{formatReg(v.registration)}</h1>
            <p className="text-foreground/80">{v.make} {v.model} {v.derivative || ""} · {customer?.name}</p>
          </div>
          <div className="flex items-center gap-2">
            {canDispose && (
              <Btn variant="outline" data-testid="cv-dispose" onClick={() => setShowDispose(true)}>Return / dispose</Btn>
            )}
            <Btn data-testid="cv-new-request" onClick={() => setShowNew(true)}><Plus className="h-4 w-4" /> New request</Btn>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <Panel className="p-5" data-testid="cv-details">
            <h2 className="font-semibold mb-2">Details</h2>
            <Row label="Current driver" value={driver?.full_name} />
            <Row label="Driver mobile" value={driver?.mobile_number} />
            <Row label="Latest mileage" value={v.current_mileage ? `${v.current_mileage.toLocaleString()} mi` : "—"} />
            <Row label="MOT due" value={fmtDate(v.mot_due_date)} />
            <Row label="Service due" value={fmtDate(v.service_due_date)} />
            <Row label="Contract ends" value={fmtDate(v.contract_end_date)} />
            <Row label="Open recall" value={v.open_recall ? (v.recall_summary || "Action required") : "None"} tone={v.open_recall ? "red" : undefined} />
            <Row label="Status" value={v.vehicle_status} />
          </Panel>

          <Panel className="p-5" data-testid="cv-current-booking">
            <h2 className="font-semibold mb-2">Current request</h2>
            {openBooking ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm">{REQUEST_LABEL[openBooking.request_type] || openBooking.request_type}</span>
                  <Tag tone="green">{openBooking.public_status}</Tag>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{openBooking.human_reference}{openBooking.appointment_at ? ` · ${fmtDate(openBooking.appointment_at)}` : ""}{openBooking.garage_name ? ` · ${openBooking.garage_name}` : ""}</div>
                {updates[0] && (
                  <div className="mt-3 p-3 rounded-[6px] bg-secondary/40" data-testid="cv-latest-update">
                    <span className="vda-tick text-[hsl(var(--vda-quiet))]">Latest Venson update</span>
                    <p className="text-sm mt-1">{updates[0].public_message}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">{fmtDateTime(updates[0].created_at)}</p>
                  </div>
                )}
                <Btn variant="outline" className="w-full mt-3" data-testid="cv-request-update" onClick={requestUpdate} loading={chasing}>
                  <RefreshCw className="h-4 w-4" /> Request an update
                </Btn>
                {updates.length > 1 && (
                  <div className="mt-4 border-t border-border pt-3" data-testid="cv-timeline">
                    <span className="vda-tick text-[hsl(var(--vda-quiet))]">Timeline</span>
                    {updates.map((u) => (
                      <div key={u.id} className="flex gap-2 py-1.5 text-sm border-b border-border last:border-0">
                        <span className="vda-marker mt-1.5 bg-[hsl(var(--vda-green))]" aria-hidden="true" />
                        <div><p>{u.public_message}</p><p className="text-[11px] text-muted-foreground">{fmtDateTime(u.created_at)}</p></div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <EmptyState title="No open request" subtitle="Raise one with New request above." />
            )}
          </Panel>
        </div>

        <Panel className="p-5 mt-5" data-testid="cv-mileage"><h2 className="font-semibold mb-2">Mileage trend</h2><MileageTrend vehicleId={v.id} /></Panel>

        <Panel className="p-0 mt-5 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-border"><h2 className="font-semibold">Request history</h2></div>
          {bookings.length === 0 ? <div className="p-5"><EmptyState title="No requests yet" /></div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead><tr className="bg-secondary/60 text-left vda-tick text-muted-foreground"><th className="px-4 py-2 font-medium">Reference</th><th className="px-4 py-2 font-medium">Type</th><th className="px-4 py-2 font-medium">Status</th><th className="px-4 py-2 font-medium">Raised</th></tr></thead>
                <tbody>{bookings.map((b) => (
                  <tr key={b.id} className="border-t border-border" data-testid={`cv-booking-${b.human_reference}`}>
                    <td className="px-4 py-2 vda-reg">{b.human_reference}</td>
                    <td className="px-4 py-2">{REQUEST_LABEL[b.request_type] || b.request_type}</td>
                    <td className="px-4 py-2"><Tag tone={b.public_status === "Completed" ? "muted" : "green"}>{b.public_status}</Tag></td>
                    <td className="px-4 py-2 text-muted-foreground">{fmtDate(b.created_at)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </Panel>
        <Panel className="p-5 mt-5" data-testid="cv-history">
          <h2 className="font-semibold mb-3">Vehicle history</h2>
          <VehicleHistory events={events} testId="cv-vehicle-history" />
        </Panel>
      </div>

      {showNew && (
        <NewRequestModal
          vehicle={v}
          hirePolicy={customer?.hire_policy}
          prefillDriver={{ name: driver?.full_name || "", mobile: driver?.mobile_number || "" }}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); toast.success("Request submitted to your Venson team."); reload(); }}
        />
      )}
      {showDispose && (
        <DisposeVehicleModal
          vehicle={v}
          onClose={() => setShowDispose(false)}
          onCreated={() => { setShowDispose(false); reload(); }}
        />
      )}
    </>
  );
}
